import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  DeviceConnectionStatus,
  DeviceConnectionStatusValues,
  Feature,
  FeatureDeprecated,
  FeatureNames,
  GatewayDevicesProperties,
} from '@vi-iot/client';

import {
  ConfigurationSelectors,
  DevicesSelectors,
  Gateway,
  GatewaySelectors,
  GatewayState,
  InstallationDetailsSelectors,
  InstallationFeaturesSelectors,
  isReady,
  RichDevice,
} from '@store';

import { AppState } from '../app.state';
import { RemoteDeviceState } from '../remote-device/remote-device.state';

import {
  COMMISSIONING_STEP,
  DeviceAssignmentResult,
  DevicePairingStatus,
  RemoteDeviceCode,
  RemoteDeviceType,
  ValidateEuidResult,
} from './commissioning.model';
import { CommissioningState } from './commissioning.state';
import {
  CommissioningDeviceAssignChannelParams,
  CommissioningDeviceAssignHeatingCircuitParams,
  CommissioningRemoteDeviceAssignHeatingCircuitParams,
  CommissioningRemoteDeviceAssignVentilationParams,
  CommissioningDeviceAssignRoomParams,
  CommissioningDeviceAssignValidationParams,
} from './commissioning.types';
import { InstallationFeaturesState } from '../installation-features/installation-features.state';

export namespace CommissioningSelectors {
  export const commissioning = (state: AppState): CommissioningState => state.commissioning;
  export const gateway = (state: AppState): GatewayState => state.gateway;
  export const remoteDevice = (state: AppState): RemoteDeviceState => state.remoteDevice;
  export const installationFeatures = (state: AppState): InstallationFeaturesState =>
    state.installationFeatures;

  export function getState(): MemoizedSelector<
    AppState,
    CommissioningState,
    DefaultProjectorFn<CommissioningState>
  > {
    return createSelector(commissioning, (state: CommissioningState) => state);
  }

  export function getFeature<T, K>(
    name: string
  ): MemoizedSelector<
    AppState,
    FeatureDeprecated<T, K>,
    DefaultProjectorFn<FeatureDeprecated<T, K>>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.feature[name] as FeatureDeprecated<T, K>
    );
  }
  export function getActiveStep(): MemoizedSelector<
    AppState,
    COMMISSIONING_STEP,
    DefaultProjectorFn<COMMISSIONING_STEP>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.devicePairing.activeStep
    );
  }

  export function getCurrentCode(): MemoizedSelector<
    AppState,
    RemoteDeviceCode,
    DefaultProjectorFn<RemoteDeviceCode>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.devicePairing.currentCode
    );
  }

  export function getPairingStatus(): MemoizedSelector<
    AppState,
    DevicePairingStatus,
    DefaultProjectorFn<DevicePairingStatus>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.devicePairing.pairingStatus
    );
  }

  export function getDeviceType(): MemoizedSelector<
    AppState,
    RemoteDeviceType,
    DefaultProjectorFn<RemoteDeviceType>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.devicePairing.deviceType
    );
  }

  // eslint-disable-next-line
  export function getLoadingIndicator() {
    return createSelector(commissioning, (state: CommissioningState) => ({
      loading: state.featureLoadingState.loading,
      loaded: state.featureLoadingState.loaded,
      performing: state.featureLoadingState.performing,
      performed: state.featureLoadingState.performed,
    }));
  }

  export function isDeviceNotAssignedToHeatingCircuit(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      ConfigurationSelectors.selectFeaturesBaseUri,
      InstallationDetailsSelectors.getInstallationId(),
      InstallationDetailsSelectors.getSelectedGateway(),
      installationFeatures,
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        featuresBaseUri: string,
        stateInstallationId: number,
        stateGatewaySerial: Gateway,
        installationFeaturesState: InstallationFeaturesState
      ) => {
        const heatingCircuitFeature = GatewaySelectors.getHeatingCircuitOfDevice(
          deviceId,
          gatewayDevices,
          installationFeaturesState,
          featuresBaseUri,
          stateInstallationId,
          stateGatewaySerial
        );

        const isDeviceTypeCorrectForHeatingCircuitCheck =
          GatewaySelectors.getIsDeviceOfType(
            deviceId,
            RemoteDeviceType.FHT_CHANNEL,
            gatewayDevices
          ) ||
          GatewaySelectors.getIsDeviceOfType(deviceId, RemoteDeviceType.FHT, gatewayDevices) ||
          GatewaySelectors.getIsDeviceOfType(deviceId, RemoteDeviceType.TRV, gatewayDevices);

        if (
          !isDeviceTypeCorrectForHeatingCircuitCheck ||
          DevicesSelectors.isStandaloneGateway(gatewayDevices) // for standalone heating circuit check doesn't apply
        ) {
          return false;
        }

        return (
          isReady(heatingCircuitFeature) &&
          !Number.isInteger(heatingCircuitFeature?.properties?.value?.value)
        );
      }
    );
  }

  export function isClimateSensorNotConnected(
    deviceId: string
  ): MemoizedSelector<AppState, boolean, DefaultProjectorFn<boolean>> {
    return createSelector(
      InstallationFeaturesSelectors.selectInstallationGatewayFeature(FeatureNames.gatewayDevices),
      InstallationFeaturesSelectors.selectInstallationDeviceFeature(
        FeatureNames.deviceConnection,
        deviceId
      ),
      (
        gatewayDevices: Feature<GatewayDevicesProperties, never>,
        connectionStatus: Feature<DeviceConnectionStatus, {}>
      ) => {
        const isClimateSensor = GatewaySelectors.getIsDeviceOfType(
          deviceId,
          RemoteDeviceType.CS,
          gatewayDevices
        );

        const noConnection =
          connectionStatus?.properties?.status?.value !== DeviceConnectionStatusValues.connected &&
          connectionStatus?.properties?.status?.value !== DeviceConnectionStatusValues.paired;

        return isClimateSensor && noConnection;
      }
    );
  }

  export function isDeviceAlreadyAdded(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.devicePairing.isAlreadyAdded
    );
  }

  export function getPairedDeviceId(): MemoizedSelector<
    AppState,
    string,
    DefaultProjectorFn<string>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.devicePairing.pairedDeviceId
    );
  }

  export function isAddingDirectlyToRoom(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.devicePairing.isSpecificRoomSelected
    );
  }

  export function getDeviceAssignmentParams(): MemoizedSelector<
    AppState,
    {
      room: CommissioningDeviceAssignRoomParams;
      heatingCircuit: CommissioningDeviceAssignHeatingCircuitParams;
      rmHeatingCircuit: CommissioningRemoteDeviceAssignHeatingCircuitParams;
      ventilation: CommissioningRemoteDeviceAssignVentilationParams;
      channels: CommissioningDeviceAssignChannelParams[];
    },
    DefaultProjectorFn<{
      room: CommissioningDeviceAssignRoomParams;
      heatingCircuit: CommissioningDeviceAssignHeatingCircuitParams;
      rmHeatingCircuit: CommissioningRemoteDeviceAssignHeatingCircuitParams;
      ventilation: CommissioningRemoteDeviceAssignVentilationParams;
      channels: CommissioningDeviceAssignChannelParams[];
    }>
  > {
    return createSelector(commissioning, (state: CommissioningState) => {
      return {
        room: state.deviceAssignment.room,
        heatingCircuit: state.deviceAssignment.heatingCircuit,
        rmHeatingCircuit: state.deviceAssignment.rmHeatingCircuit,
        ventilation: state.deviceAssignment.ventilation,
        channels: state.deviceAssignment.channels,
      };
    });
  }

  export function getPairedDeviceFhtChannels(
    deviceId: string
  ): MemoizedSelector<AppState, RichDevice[], DefaultProjectorFn<RichDevice[]>> {
    return createSelector(
      commissioning,
      DevicesSelectors.getFhtDeviceChannels(deviceId),
      (_state: CommissioningState, channels: RichDevice[]): RichDevice[] => {
        return channels;
      }
    );
  }

  export function getValidateEuidResult(): MemoizedSelector<
    AppState,
    ValidateEuidResult,
    DefaultProjectorFn<ValidateEuidResult>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.devicePairing.euidValidationResult
    );
  }

  export function getAssignmentResult(): MemoizedSelector<
    AppState,
    DeviceAssignmentResult,
    DefaultProjectorFn<DeviceAssignmentResult>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.deviceAssignment.assignmentResult
    );
  }

  export function isAddDeviceDisabled(): MemoizedSelector<
    AppState,
    boolean,
    DefaultProjectorFn<boolean>
  > {
    return createSelector(
      commissioning,
      InstallationFeaturesSelectors.selectFeaturesForAllGatewayLoading,
      (state: CommissioningState, areFeaturesForAllGatewayLoading: boolean) => {
        return (
          state.featureLoadingState.loading ||
          state.featureLoadingState.performing ||
          areFeaturesForAllGatewayLoading
        );
      }
    );
  }

  export function getAssignmentValidationResultErrors(): MemoizedSelector<
    AppState,
    CommissioningDeviceAssignValidationParams[],
    DefaultProjectorFn<CommissioningDeviceAssignValidationParams[]>
  > {
    return createSelector(
      commissioning,
      (state: CommissioningState) => state.deviceAssignment.validationResult
    );
  }
}
